import { Component, OnInit } from '@angular/core';
import { HisNotificationService, HisUiService, GenericValue } from 'hisng';
import { PublicationType } from '../../../model/publication-type';
import { PublicationTypeRelation } from '../../../model/publication-type-relation';
import { PublicationTypeRelationService } from '../../../service/publication-type-relation.service';
import { PublicationModuleOptions } from '../../../publication-module.options';

@Component({
    selector: 'res-publication-configuration',
    templateUrl: './publication-configuration.component.html'
})
export class PublicationConfigurationComponent implements OnInit {

    public readonly moduleOptions = PublicationModuleOptions;

    public publicationTypeRelationships: {
        parent: PublicationType; childs: PublicationType[]; dbRelationships: PublicationTypeRelation[];
    }[];

    // workaround: since the tooltip of the delete-button is not refreshed after the parenttype has changed
    // we disable the button for one tick to force a redraw
    public doRefresh = false;
    private refreshInterval: any;

    constructor(
        private publicationTypeRelationService: PublicationTypeRelationService,
        private notificationService: HisNotificationService,
        private hisUiService: HisUiService
    ) { }

    ngOnInit() {
        this.reset();
    }

    savePublicationTypeRelation() {
        if (!this.duplicateCheck()) {
            return;
        }

        const relationships: PublicationTypeRelation[] = [];

        this.publicationTypeRelationships.forEach(rel => {
            if (!rel.childs.length) {
                relationships.push({ id: null, objGuid: null, parent: rel.parent, child: null });
            }
            rel.childs.forEach(c => {
                let dbRel = rel.dbRelationships.find(r => r.child.id === c.id);
                if (dbRel) {
                    dbRel.parent = rel.parent;
                    dbRel.child = c;
                } else {
                    dbRel = { id: null, objGuid: null, parent: rel.parent, child: c };
                }
                relationships.push(dbRel);
            });
        });

        this.publicationTypeRelationService.savePublicationTypeRelations(relationships)
            .subscribe( (result: PublicationTypeRelation[]) => {
                this.setPublicationTypeRelationships(result);
                if (result) {
                    const successMsg = 'fs.res.publication.configuration.publicationTypeRelation.success.edit';
                    this.notificationService.successI18nRes(successMsg);
                }
            });
    }

    duplicateCheck() {
        let errorMsg: string = null;
        const parentIds: number[] = [];
        const duplicateParentIds: number[] = [];
        const message: string[] = [];
        this.publicationTypeRelationships.forEach(rel => {
            if (rel.parent && parentIds.indexOf(rel.parent.id) === -1) {
                parentIds.push(rel.parent.id);
            } else if (rel.parent && !duplicateParentIds.some(dP => dP === rel.parent.id)) {
                errorMsg = 'fs.res.publication.configuration.publicationTypeRelation.error.noDuplicateParents';
                message.push(this.hisUiService.translate(errorMsg, rel.parent.defaulttext));
                duplicateParentIds.push(rel.parent.id);
            }
            const childIds: number[] = [];
            const duplicatechildIds: number[] = [];
            rel.childs.forEach(child => {
                if (child && childIds.indexOf(child.id) === -1) {
                    childIds.push(child.id);
                } else if (child && rel.parent && !duplicatechildIds.some(dC => dC === child.id)) {
                    errorMsg = 'fs.res.publication.configuration.publicationTypeRelation.error.noDuplicateChilds';
                    message.push(this.hisUiService.translate(errorMsg, child.defaulttext, rel.parent.defaulttext));
                    duplicatechildIds.push(child.id);
                }
            });
        });

        if (errorMsg) {
            this.notificationService.error(message, this.hisUiService.translate('global.validationerror'));
        }

        return !errorMsg;
    }

    reset() {
        this.publicationTypeRelationService.getAllPublicationTypeRelations().subscribe( (relationships: PublicationTypeRelation[]) => {
            this.setPublicationTypeRelationships(relationships);
        });
    }

    setPublicationTypeRelationships(relationships: PublicationTypeRelation[]) {
        this.publicationTypeRelationships = [];
        relationships.forEach(rel => {
            const relEntry = this.publicationTypeRelationships.find(r => r.parent.id === rel.parent.id);
            if (!relEntry) {
                this.publicationTypeRelationships.push({ parent: rel.parent, childs: [rel.child], dbRelationships: [rel] });
            } else {
                const entry = this.publicationTypeRelationships.find(r => r.parent.id === relEntry.parent.id);
                entry.childs.push(rel.child);
                entry.dbRelationships.push(rel);
            }
        });
    }

    onParentPublicationTypeChange(parent: PublicationType, index: number) {
        this.publicationTypeRelationships[index].parent = parent;

        this.doRefresh = true;
        this.refreshInterval = setInterval(() => {
            this.doRefresh = false;
            clearInterval(this.refreshInterval);
        }, 0);
    }

    onChildPublicationTypeChange(childs: PublicationType[], index: number) {
        this.publicationTypeRelationships[index].childs = childs;
    }

    addNewRelationship() {
        this.publicationTypeRelationships.push({ parent: null, childs: [], dbRelationships: [] });
    }

    deleteRelationship(index: number) {
        this.publicationTypeRelationships.splice(index, 1);
    }

    getDeleteButtonToolTip(parentPublicationType: GenericValue): string {
        if (parentPublicationType) {
            return this.hisUiService.translate('fs.res.publication.configuration.publicationTypeRelation.deleteConfiguration',
                parentPublicationType.defaulttext);
        } else {
            return this.hisUiService.translate('fs.res.publication.configuration.publicationTypeRelation.deleteConfigurationEmpty');
        }
    }
}
